import React, { useEffect, useState } from "react";
import { Col, Form, Row, Spinner, Badge } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { authHeader } from "../../../services/auth-header";
import { BASE_URL } from "../../../services/Api";
import debounce from 'lodash/debounce';
import CommonSalesQuotationInternationalFields from "../PumpInternationalQuotation/CommonSalesQuotationInternationalFields";

const PumpInternationalQuotationConversion = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        customer: null,
        customerDetails: {
            contactperson: '',
            contactno: '',
            email: '',
            freight: '',
            packingandforwarding: '',
            paymentterm: '',
            incoterms: ''
        },
        customerMultipleAddress: null,
        rows: [],
        exchangeRate: 1,
        exportchargescurrency: 0,
        exportpreparationchargesinr: '',
        selectedCurrency: " "

    });
    const [quotationno, setQuotationno] = useState('');
    const [quotationdate, setQuotationdate] = useState('');
    const [quotevalidity, setQuotevalidity] = useState('');
    const [status, setStatus] = useState('Open');
    const [enquiryValidationMsg, setEnquiryValidationMsg] = useState('');
    const [isEnquiryValid, setIsEnquiryValid] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);


    const checkquotationno = async (number) => {
        if (!number) {
            setEnquiryValidationMsg('');
            setIsEnquiryValid(false);
            return;
        }

        if (!/^[A-Za-z0-9-]+$/.test(number)) {
            setEnquiryValidationMsg('❌ Only alphanumeric characters and hyphens are allowed');
            setIsEnquiryValid(false);
            return;
        }

        try {
            const response = await axios.get(`${BASE_URL}quotation/check/${number}`, {
                headers: authHeader(),
            });

            if (response.data === true) {
                setEnquiryValidationMsg('❌ This enquiry number already exists.');
                setIsEnquiryValid(false);
            } else if (response.data === false) {
                setEnquiryValidationMsg('✅ This enquiry number is available.');
                setIsEnquiryValid(true);
            } else {
                setEnquiryValidationMsg('Unexpected response from server.');
                setIsEnquiryValid(false);
            }
        } catch (error) {
            console.error("Error checking enquiry number:", error);
            setEnquiryValidationMsg('❌ Error validating enquiry number.');
            setIsEnquiryValid(false);
        }
    };


    useEffect(() => {
        const debouncedCheck = debounce(() => {
            checkquotationno(quotationno);
        }, 400);

        debouncedCheck();

        return () => debouncedCheck.cancel();
    }, [quotationno]);


    const fetchQuotationData = async () => {
        try {
            const response = await axios.get(`${BASE_URL}enquiry/findById/${id}`, {
                headers: authHeader()
            });

            const data = response.data;

            setQuotationno(data.quotationno);
            setQuotationdate(data.quotationdate?.split('T')[0] || '');
            setQuotevalidity(data.quotevalidity || '');
            setStatus(data.status || 'Open');

            setFormData({
                customer: data.customer ? {
                    value: data.customer.id,
                    label: data.customer.companyname
                } : null,
                customerDetails: {
                    contactperson: data.contactperson || '',
                    contactno: data.contactno || '',
                    email: data.email || '',
                    paymentterm: data.customer?.paymentterm || '',
                    incoterms: data.customer?.incoterms || ''
                },
                customerMultipleAddress: data.customeraddress ? {
                    value: data.customeraddress.id,
                    label: data.customeraddress.customeraddress
                } : null,
                rows: data.enquiryItems?.map(item => ({
                    value: item.product?.id,
                    label: item.product?.description,
                    description: item.product?.description || '',
                    productremarks: item.productremarks || '',
                    quantity: item.quantity || 0,
                    unitratecurrency: item.unitratecurrency || 0,
                    totalcurrency: item.totalcurrency || 0,
                    exchangerate: item.exchangerate || data.quotationexchangerate || 1,
                    unitRateInr: item.unitrateinr || 0,
                    totalInr: item.totalinr || 0,
                    hsnsacno: item.hsnsacno || '',
                    partno: item.partno || '',
                    serialnumber: item.serialnumber || '',
                    modelnumber: item.modelnumber || '',
                    tagnumber: item.tagnumber || '',
                    moc: item.moc || '',
                    itemcode: item.itemcode || '',
                    uom: item.uom || '',
                    currency: item.currency || data.quotationcurrency || "$ (USD)"
                })) || [],
                exchangeRate: data.quotationexchangerate || 1,
                exportchargescurrency: data.exportpreparationchargescurrency || 0,
                exportpreparationchargesinr: data.exportpreparationchargesinr || '',
                selectedCurrency: data.quotationcurrency || "$ (USD)"
            });

        } catch (error) {
            console.error("Error fetching quotation data", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to fetch quotation data. Please try again.",
            });
            navigate("/quotationIndexTable/1");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchQuotationData();
    }, [id]);


    const handleDataChange = (newData) => {
        setFormData(prev => ({
            ...prev,
            ...newData,
            rows: newData.rows || prev.rows
        }));
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setFormSubmitted(true);
        if (!isEnquiryValid) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please enter a valid enquiry number before submitting.',
            });
            return;
        }

        const {
            customer,
            customerDetails,
            customerMultipleAddress,
            rows,
            exchangeRate,
            exportchargescurrency,
            exportpreparationchargesinr,
            selectedCurrency
        } = formData;

        if (!customer) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Please select a customer',
            });
            setIsSubmitting(false);
            return;
        }

        if (rows.length === 0) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Please add at least one product',
            });
            setIsSubmitting(false);
            return;
        }

        // Calculate totals
        const misccharges = exportchargescurrency * exchangeRate;
        const netTotalInr = rows.reduce((total, row) => total + (row.totalInr || 0), 0);
        const netTotalCurrency = rows.reduce((total, row) => total + (row.totalcurrency || 0), 0);
        const grandTotalInr = netTotalInr + misccharges;
        const grandTotalCurrency = netTotalCurrency + exportchargescurrency;

        // Prepare quotation items
        const quotationItems = rows.map(row => ({
            product: { id: row.value },
            productremarks: row.productremarks || '',
            quantity: row.quantity || 0,
            unitrateinr: row.unitRateInr || 0,
            totalinr: row.totalInr || 0,
            unitratecurrency: row.unitratecurrency || 0,
            totalcurrency: row.totalcurrency || 0,
            exchangerate: row.exchangerate || exchangeRate,
            itemcode: row.itemcode || '',
            hsnsacno: row.hsnsacno || '',
            serialnumber: row.serialnumber || '',
            modelnumber: row.modelnumber || '',
            tagnumber: row.tagnumber || '',
            moc: row.moc || '',
            partno: row.partno || '',
            uom: row.uom || '',
            currency: selectedCurrency,
        }));

        // Prepare main quotation data
        const quotationData = {
            quotationtype: 'Pump Spare',
            quotationcategory: 'International',
            quotationno,
            quotationdate,
            quotevalidity,
            status,
            quotationItems,
            customer: { id: customer.value },
            customeraddress: customerMultipleAddress ? { id: customerMultipleAddress.value } : null,
            contactperson: customerDetails.contactperson,
            contactno: customerDetails.contactno,
            email: customerDetails.email,
            freight: customerDetails.freight,
            packingandforwarding: customerDetails.packingandforwarding,
            paymentterm: customerDetails.paymentterm,
            incoterms: customerDetails.incoterms,
            quotationcurrency: selectedCurrency,
            quotationexchangerate: exchangeRate,
            grandtotalinr: grandTotalInr,
            grandtotalcurrency: grandTotalCurrency,
            exportpreparationchargescurrency: exportchargescurrency,
            exportpreparationchargesinr,
            nettotalinr: netTotalInr,
            nettotalcurrency: netTotalCurrency,
        };

        try {
            await axios.post(`${BASE_URL}quotation/enquirytoquotation/${id}`, quotationData, {
                headers: authHeader()
            });

            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Quotation updated successfully!',
            });
            navigate("/quotationIndexTable/1");
        } catch (error) {
            console.error("Error updating quotation:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to update quotation. Please try again.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="container text-center py-5">
                <Spinner animation="border" />
                <p>Loading quotation details...</p>
            </div>
        );
    }

    return (
        <div className="container">
            <Form>
                <Row>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Quotation Number: *</Form.Label>
                            <Form.Control
                                type="text"
                                value={quotationno}
                                onChange={e => {
                                    setQuotationno(e.target.value);
                                    setFormSubmitted(false);
                                }}
                                isInvalid={
                                    (formSubmitted && !isEnquiryValid) ||
                                    enquiryValidationMsg.includes('exists') ||
                                    enquiryValidationMsg.includes('Error') ||
                                    enquiryValidationMsg.includes('allowed')
                                }
                                required
                            />
                            {enquiryValidationMsg && (
                                <Form.Control.Feedback type={
                                    enquiryValidationMsg.includes('exists') ||
                                        enquiryValidationMsg.includes('Error') ||
                                        enquiryValidationMsg.includes('allowed') ?
                                        "invalid" : "valid"
                                }>
                                    {enquiryValidationMsg}
                                </Form.Control.Feedback>
                            )}
                            {formSubmitted && !isEnquiryValid && !enquiryValidationMsg && (
                                <Form.Control.Feedback type="invalid">
                                    Please enter a valid enquiry number
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Quotation Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={quotationdate}
                                onChange={(e) => setQuotationdate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Quote validity</Form.Label>
                            <Form.Control
                                type="text"
                                value={quotevalidity}
                                onChange={(e) => setQuotevalidity(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <CommonSalesQuotationInternationalFields
                    onDataChange={handleDataChange}
                    initialData={formData}
                    isUpdateMode={true}
                />

                <br />
                <div className="d-flex justify-content-end">
                    <button
                        className="btn btn-primary me-2"
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" />
                                Updating...
                            </>
                        ) : 'CONVERT QUOTATION'}
                    </button>

                    <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={() => navigate("/quotationIndexTable/1")}
                        disabled={isSubmitting}
                    >
                        CANCEL
                    </button>
                </div>
            </Form>
        </div>
    );
};

export default PumpInternationalQuotationConversion;